import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { LeadForm } from "@/components/home/LeadForm";
import { BOOKING_URL } from "@/lib/booking";

const HERO_EASE = [0.22, 1, 0.36, 1];
const HERO_SCROLL_RANGE = [0, 900];

export function HomeHero({ settings, projects = [] }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const mediaY = useTransform(scrollY, HERO_SCROLL_RANGE, [0, reduceMotion ? 0 : 46]);
  const slides = useMemo(() => {
    const hamptons = projects.find((project) => /southampton|hampton/i.test(project.title || ""));
    const gallery = [hamptons?.cover_image, ...(hamptons?.images || [])].filter(Boolean);
    return [...new Set(gallery)].slice(0, 10);
  }, [projects]);
  useEffect(() => {
    setSlideIndex(0);
    if (reduceMotion || slides.length < 2) return undefined;
    const interval = window.setInterval(() => setSlideIndex((current) => (current + 1) % slides.length), 6500);
    return () => window.clearInterval(interval);
  }, [reduceMotion, slides.length]);
  const mediaInitial = useMemo(() => reduceMotion ? false : { scale: 1 }, [reduceMotion]);
  const mediaAnimate = useMemo(() => ({ scale: reduceMotion ? 1 : 1.045 }), [reduceMotion]);
  const mediaTransition = useMemo(() => ({ duration: reduceMotion ? 0 : 15, ease: "easeOut" }), [reduceMotion]);
  const copyInitial = useMemo(() => reduceMotion ? false : { opacity: 0, y: 28 }, [reduceMotion]);
  const copyTransition = useMemo(() => ({ duration: reduceMotion ? 0 : 1.1, ease: HERO_EASE, delay: reduceMotion ? 0 : 0.18 }), [reduceMotion]);
  const panelInitial = useMemo(() => reduceMotion ? false : { opacity: 0, y: 20 }, [reduceMotion]);
  const panelTransition = useMemo(() => ({ duration: reduceMotion ? 0 : 1, ease: HERO_EASE, delay: reduceMotion ? 0 : 0.48 }), [reduceMotion]);
  return (
    <section data-testid="home-hero" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#0B0C0E] text-white">
      <motion.div data-testid="hero-slideshow" style={{ y: mediaY }} initial={mediaInitial} animate={mediaAnimate} transition={mediaTransition} className="absolute -inset-10">
        {slides.map((image, index) => <img key={image} data-testid={`hero-slide-${index + 1}`} src={image} alt="" className={`absolute inset-0 h-full w-full object-cover opacity-0 transition-[opacity,transform] [transition-duration:1800ms] ease-out ${index === slideIndex ? "opacity-80 scale-[1.055]" : "scale-100"}`} />)}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/25 to-black/20" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0C0E] to-transparent" />

      <div className="relative h-full flex flex-col justify-end px-6 pb-14 sm:px-10 sm:pb-20 lg:px-16">
        <motion.div initial={copyInitial} animate={{ opacity: 1, y: 0 }} transition={copyTransition} className="max-w-5xl">
          <div data-testid="hero-location-label" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs uppercase tracking-[.16em] text-white/95"><span className="h-px w-9 bg-[#E5B869]" /><span data-testid="hero-service-area">Serving New York City · Long Island · Hamptons · Hudson Valley · Miami · Houston</span></div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-7 max-w-5xl leading-[.88] text-white">{settings.hero_headline}</h1>
          <p data-testid="hero-subline" className="text-white/72 mt-7 text-sm md:text-lg tracking-wide max-w-xl">{settings.hero_subline}</p>
          <div className="flex flex-wrap gap-4 mt-10">
            <a href={BOOKING_URL} data-testid="hero-book-btn" className="btn-light">Book a Shoot</a>
            <Link to="/work" data-testid="hero-work-btn" className="btn-outline-light">View Work</Link>
          </div>
        </motion.div>
        <motion.div initial={panelInitial} animate={{ opacity: 1, y: 0 }} transition={panelTransition}
          className="hidden lg:block lg:absolute lg:bottom-20 lg:right-16 lg:w-[380px]">
          <div data-testid="hero-lead-panel" className="border border-white/15 bg-[#0B0C0E]/80 p-5 shadow-2xl shadow-black/30 backdrop-blur-xl xl:p-6">
            <LeadForm dark />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function LeadSection() {
  return (
    <section data-testid="lead-section" className="lg:hidden px-6 sm:px-10 py-20 border-b border-white/10 bg-[#121418]">
      <div className="max-w-md">
        <LeadForm />
      </div>
    </section>
  );
}
